/*
 * <reviews-wall> Custom Element.
 *
 * Data source: WiseReviews public proxy API
 * (https://wise-reviews.codexapps.co/api/proxy/reviews). Migrated from
 * Judge.me on 2026-07-16 (see .claude/plans/2026-07-16-reviews-page-wisereviews-integration-plan.md).
 *
 * Attributes:
 *   data-endpoint    - base URL (default: WiseReviews proxy). Required.
 *   data-shop        - shop.permanent_domain (required for the API's ?shop= param).
 *   data-max         - initial per_page (default 24).
 *   data-sort        - WiseReviews sort_by (default RECOMMENDED).
 *   data-featured    - "1" to filter to featured reviews only (default "1").
 *   data-accents     - CSV of accent token stems (default "tomato-400,blue-400,magenta-400,sun-400").
 *
 * Behavior:
 *   1. connectedCallback fires two parallel fetches:
 *      a. Aggregate stats (per_page=1, no featured filter) - feeds the hero pill.
 *      b. Featured reviews page 1 - feeds the masonry wall.
 *   2. Pill hydration: locate elements with data-live-hydrate="rating|count|stars|fill"
 *      inside document, update textContent + --fill-percent CSS var.
 *   3. Wall hydration: build cards via createElement + createTextNode, replace seed fallback.
 *   4. If meta.last_page > 1, reveal the sibling `[data-reviews-load-more]` button.
 *      On click, fetch the next page and append cards; hide button when done.
 *   5. On error at any stage, keep SSR fallback intact for that surface.
 *
 * Coupling:
 *   If WiseReviews changes their proxy URL or response shape, only _buildAggUrl,
 *   _buildFeaturedUrl, and _mapReview need updating. The card DOM contract is stable.
 *
 * XSS safety:
 *   All user-controlled strings set via textContent, never innerHTML.
 *   Image src validated via _isSafeHttpsUrl.
 *
 * Cache:
 *   sessionStorage keys wise_agg_stats, wise_featured_page_N. 15-minute TTL.
 */

const CACHE_TTL_MS = 15 * 60 * 1000;
const FETCH_TIMEOUT_MS = 8000;
const SHOPEE_PREAMBLE_RE = /^[A-Z][a-zA-Z]+:\s+[A-Za-z ]+$/;

class ReviewsWall extends HTMLElement {
  constructor() {
    super();
    this._controller = new AbortController();
    this._loadMoreBtn = null;
    this._currentPage = 1;
    this._lastPage = 1;
    this._perPage = 24;
    this._sort = 'RECOMMENDED';
    this._accents = ['tomato-400', 'blue-400', 'magenta-400', 'sun-400'];
    this._endpoint = 'https://wise-reviews.codexapps.co/api/proxy/reviews';
    this._shop = '';
    this._featured = '1';
    this._productMap = {};
    this._lightbox = null;
    this._lightboxImg = null;
    this._lightboxPrevBtn = null;
    this._lightboxNextBtn = null;
    this._lightboxPhotos = []; // array of { src, alt } collected from rendered cards
    this._lightboxIndex = 0;
  }

  connectedCallback() {
    this._endpoint = this.dataset.endpoint || this._endpoint;
    this._shop = this.dataset.shop || '';
    this._perPage = parseInt(this.dataset.max, 10) || this._perPage;
    this._sort = this.dataset.sort || this._sort;
    this._featured = this.dataset.featured || this._featured;
    this._accents = (this.dataset.accents || this._accents.join(',')).split(',');

    if (!this._shop) {
      this._showFallback('no shop domain');
      return;
    }

    this._productMap = this._readProductMap();
    this._loadMoreBtn = this._findLoadMoreButton();
    if (this._loadMoreBtn) {
      this._loadMoreBtn.addEventListener('click', this._onLoadMoreClick);
    }
    this._setupLightbox();

    // Defer to microtask (or DOMContentLoaded) so we don't block whatever else runs in this tick.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._boot(), { once: true });
    } else {
      queueMicrotask(() => this._boot());
    }
  }

  disconnectedCallback() {
    this._controller.abort();
    if (this._loadMoreBtn) {
      this._loadMoreBtn.removeEventListener('click', this._onLoadMoreClick);
      this._loadMoreBtn = null;
    }
    this._teardownLightbox();
  }

  _setupLightbox() {
    const section = this.closest('.reviews-wall__inner') || this.parentElement;
    if (!section) return;
    this._lightbox = section.querySelector('[data-reviews-lightbox]');
    if (!this._lightbox) return;
    this._lightboxImg = this._lightbox.querySelector('[data-lightbox-img]');
    this._lightboxPrevBtn = this._lightbox.querySelector('[data-lightbox-prev]');
    this._lightboxNextBtn = this._lightbox.querySelector('[data-lightbox-next]');
    const closeBtn = this._lightbox.querySelector('[data-lightbox-close]');

    // Delegated click handler on the wall for review-card photos.
    this.addEventListener('click', this._onCardPhotoClick);

    if (closeBtn) closeBtn.addEventListener('click', this._onLightboxClose);
    if (this._lightboxPrevBtn) this._lightboxPrevBtn.addEventListener('click', this._onLightboxPrev);
    if (this._lightboxNextBtn) this._lightboxNextBtn.addEventListener('click', this._onLightboxNext);
    // Click on the dialog background (not the image or buttons) closes the lightbox.
    this._lightbox.addEventListener('click', this._onLightboxBackdropClick);
  }

  _teardownLightbox() {
    if (!this._lightbox) return;
    this.removeEventListener('click', this._onCardPhotoClick);
    const closeBtn = this._lightbox.querySelector('[data-lightbox-close]');
    if (closeBtn) closeBtn.removeEventListener('click', this._onLightboxClose);
    if (this._lightboxPrevBtn) this._lightboxPrevBtn.removeEventListener('click', this._onLightboxPrev);
    if (this._lightboxNextBtn) this._lightboxNextBtn.removeEventListener('click', this._onLightboxNext);
    this._lightbox.removeEventListener('click', this._onLightboxBackdropClick);
    this._lightbox = null;
    this._lightboxImg = null;
    this._lightboxPrevBtn = null;
    this._lightboxNextBtn = null;
  }

  _refreshLightboxPhotos() {
    // Snapshot all currently-rendered review card photos in DOM order.
    const imgs = this.querySelectorAll(':scope > .review-card .review-card__photo');
    this._lightboxPhotos = Array.from(imgs).map((el) => ({
      src: el.getAttribute('src') || '',
      alt: el.getAttribute('alt') || '',
    }));
  }

  _onCardPhotoClick = (event) => {
    const photo = event.target && event.target.closest ? event.target.closest('.review-card__photo') : null;
    if (!photo) return;
    if (!this._lightbox) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    this._refreshLightboxPhotos();
    // Find the index of the clicked photo in the current snapshot.
    const src = photo.getAttribute('src') || '';
    const idx = this._lightboxPhotos.findIndex((p) => p.src === src);
    this._lightboxIndex = idx >= 0 ? idx : 0;
    this._renderLightboxSlide();
    if (typeof this._lightbox.showModal === 'function') {
      this._lightbox.showModal();
    } else {
      this._lightbox.setAttribute('open', '');
    }
  };

  _renderLightboxSlide() {
    if (!this._lightboxImg || this._lightboxPhotos.length === 0) return;
    const slide = this._lightboxPhotos[this._lightboxIndex];
    if (!slide) return;
    this._lightboxImg.setAttribute('src', slide.src);
    this._lightboxImg.setAttribute('alt', slide.alt);
    // Hide prev/next when at edges of a single-item set; show otherwise.
    const many = this._lightboxPhotos.length > 1;
    if (this._lightboxPrevBtn) {
      if (many) this._lightboxPrevBtn.removeAttribute('hidden');
      else this._lightboxPrevBtn.setAttribute('hidden', '');
    }
    if (this._lightboxNextBtn) {
      if (many) this._lightboxNextBtn.removeAttribute('hidden');
      else this._lightboxNextBtn.setAttribute('hidden', '');
    }
  }

  _onLightboxClose = (event) => {
    if (event) event.preventDefault();
    if (!this._lightbox) return;
    if (typeof this._lightbox.close === 'function') this._lightbox.close();
    else this._lightbox.removeAttribute('open');
  };

  _onLightboxPrev = (event) => {
    if (event) event.preventDefault();
    if (this._lightboxPhotos.length === 0) return;
    this._lightboxIndex = (this._lightboxIndex - 1 + this._lightboxPhotos.length) % this._lightboxPhotos.length;
    this._renderLightboxSlide();
  };

  _onLightboxNext = (event) => {
    if (event) event.preventDefault();
    if (this._lightboxPhotos.length === 0) return;
    this._lightboxIndex = (this._lightboxIndex + 1) % this._lightboxPhotos.length;
    this._renderLightboxSlide();
  };

  _onLightboxBackdropClick = (event) => {
    // <dialog> reports its own bounding rect for the "content area". Clicks
    // outside that rect (i.e. on the backdrop) can be detected by comparing
    // event.target to the dialog element itself (backdrop clicks fire with
    // target === dialog per the HTML spec).
    if (event.target === this._lightbox) {
      this._onLightboxClose(event);
    }
  };

  _readProductMap() {
    try {
      const el = document.querySelector('script[type="application/json"][data-product-map]');
      if (!el) return {};
      return JSON.parse(el.textContent || '{}');
    } catch {
      return {};
    }
  }

  _findLoadMoreButton() {
    // Look in the section wrapper, since the button is a sibling of <reviews-wall>.
    const section = this.closest('.reviews-wall__inner') || this.parentElement;
    return section ? section.querySelector('[data-reviews-load-more]') : null;
  }

  async _boot() {
    const [agg, page1] = await Promise.all([this._loadAggregate(), this._loadFeaturedPage(1)]);
    if (agg) this._hydratePill(agg);
    if (page1) {
      this._renderInitial(page1);
      if (page1.meta && page1.meta.last_page > page1.meta.current_page) {
        this._lastPage = page1.meta.last_page;
        this._currentPage = page1.meta.current_page;
        this._showLoadMore();
      }
    } else {
      this._showFallback('featured page 1 failed');
    }
  }

  _buildAggUrl() {
    const p = new URLSearchParams({
      shop: this._shop,
      per_page: '1',
      sort_by: this._sort,
      preview: 'false',
      deviceType: 'desktop',
    });
    return `${this._endpoint}?${p.toString()}`;
  }

  _buildFeaturedUrl(page) {
    const p = new URLSearchParams({
      shop: this._shop,
      featured: this._featured,
      sort_by: this._sort,
      per_page: String(this._perPage),
      page: String(page),
      preview: 'false',
      deviceType: 'desktop',
    });
    return `${this._endpoint}?${p.toString()}`;
  }

  async _fetchJson(url, timeoutMs) {
    const timer = setTimeout(() => this._controller.abort(), timeoutMs || FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, { signal: this._controller.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`status ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  }

  _readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.cachedAt) return null;
      if (Date.now() - new Date(parsed.cachedAt).getTime() > CACHE_TTL_MS) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  _writeCache(key, obj) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ...obj, cachedAt: new Date().toISOString() }));
    } catch {
      // Quota exceeded or storage disabled - non-fatal.
    }
  }

  async _loadAggregate() {
    const cached = this._readCache('wise_agg_stats');
    if (cached) return cached;
    try {
      const json = await this._fetchJson(this._buildAggUrl());
      const avg = parseFloat(json.average_rating);
      const count = json && json.meta && Number.isInteger(json.meta.total) ? json.meta.total : 0;
      const clean = { avg: Number.isFinite(avg) ? Math.max(0, Math.min(5, avg)) : 0, count };
      this._writeCache('wise_agg_stats', clean);
      return clean;
    } catch (err) {
      if (err && err.name === 'AbortError') return null;
      // eslint-disable-next-line no-console
      console.error('[reviews-wall]', 'agg-fetch-failed', String(err));
      return null;
    }
  }

  async _loadFeaturedPage(page) {
    const cacheKey = `wise_featured_page_${page}`;
    const cached = this._readCache(cacheKey);
    if (cached) return cached;
    try {
      const json = await this._fetchJson(this._buildFeaturedUrl(page));
      const clean = {
        data: Array.isArray(json.data) ? json.data : [],
        meta: json.meta || { current_page: page, last_page: page },
      };
      this._writeCache(cacheKey, clean);
      return clean;
    } catch (err) {
      if (err && err.name === 'AbortError') return null;
      // eslint-disable-next-line no-console
      console.error('[reviews-wall]', 'featured-fetch-failed', String(err), 'page', page);
      return null;
    }
  }

  _hydratePill(agg) {
    if (!agg || !agg.count) return; // no data - leave SSR fallback
    const scope = document; // pill is outside <reviews-wall>, in the page hero
    const ratingEl = scope.querySelector('[data-live-hydrate="rating"]');
    const countEl = scope.querySelector('[data-live-hydrate="count"]');
    const starsEl = scope.querySelector('[data-live-hydrate="stars"]');
    const pillRoot = starsEl ? starsEl.closest('.hero-stat-pill') : null;

    const avgStr = agg.avg.toFixed(1);
    const countStr = this._formatCount(agg.count);

    if (ratingEl) {
      // Preserve the localized rating suffix (e.g. " / 5") by only replacing the number portion.
      // The SSR content is "{avg} / 5"; we swap the leading avg number and keep the rest.
      const existing = ratingEl.textContent || '';
      const withoutLeadingNum = existing.replace(/^\s*[0-9.]+/, '').trim();
      ratingEl.textContent = `${avgStr} ${withoutLeadingNum}`.replace(/\s+/g, ' ').trim();
    }
    if (countEl) {
      countEl.textContent = `${countStr} verified reviews`;
    }
    if (pillRoot) {
      const percent = Math.round((agg.avg / 5) * 100);
      pillRoot.style.setProperty('--fill-percent', `${percent}%`);
    }
    if (starsEl) {
      starsEl.setAttribute('aria-label', `Rated ${avgStr} out of 5 based on ${countStr} reviews`);
    }
  }

  _renderInitial(page) {
    const cards = this._mapCards(page.data, 0);
    const frag = document.createDocumentFragment();
    cards.forEach((c) => frag.appendChild(c));
    // Preserve any @app block children AND the seed fallback; remove noscript.
    const appBlocks = Array.from(this.querySelectorAll(':scope > .shopify-app-block, :scope > [data-app-block]'));
    Array.from(this.childNodes).forEach((n) => {
      if (n.nodeType !== Node.ELEMENT_NODE) { this.removeChild(n); return; }
      if (n.tagName === 'NOSCRIPT') { this.removeChild(n); }
      if (n.classList && n.classList.contains('reviews-wall__seed-fallback')) { this.removeChild(n); }
    });
    this.appendChild(frag);
    appBlocks.forEach((el) => this.appendChild(el));
  }

  _appendPage(page) {
    const startIndex = this.querySelectorAll(':scope > .review-card').length;
    const cards = this._mapCards(page.data, startIndex);
    const frag = document.createDocumentFragment();
    cards.forEach((c) => frag.appendChild(c));
    this.appendChild(frag);
  }

  _mapCards(reviews, startIndex) {
    if (!Array.isArray(reviews)) return [];
    const out = [];
    reviews.forEach((r, i) => {
      const accent = this._accentForProductId(r && r.product_id);
      out.push(this._buildCard(this._mapReview(r), accent));
    });
    return out;
  }

  _accentForProductId(productId) {
    // Product-based hash so all cards for the same product render in the same
    // accent color. Shopify product IDs are sequential (10537xxx, 10541xxx...)
    // so naive `id % 4` clumps most products into the same bucket. Summing
    // character codes distributes better across the 4-color palette.
    const s = String(productId == null ? '' : productId);
    let sum = 0;
    for (let i = 0; i < s.length; i++) sum += s.charCodeAt(i);
    return this._accents[sum % this._accents.length];
  }

  _mapReview(r) {
    // WiseReviews response shape -> our card contract.
    const body = this._cleanBody(String(r.body || ''));
    const tag = this._productMap[String(r.product_id)] || '';
    const photo = Array.isArray(r.media_urls) && r.media_urls[0] ? r.media_urls[0] : null;
    return {
      name: String(r.reviewer_name || ''),
      quote: body,
      rating: Math.max(1, Math.min(5, parseInt(r.rating, 10) || 5)),
      tag,
      photoUrl: photo,
    };
  }

  _cleanBody(body) {
    // Strip leading Shopee/Lazada preamble lines like "Suitability: Good\nQuality: Good\n".
    const lines = body.split(/\r?\n/);
    let i = 0;
    while (i < lines.length && SHOPEE_PREAMBLE_RE.test(lines[i].trim())) i++;
    return lines.slice(i).join('\n').trim();
  }

  _buildCard(mapped, accent) {
    const article = document.createElement('article');
    article.className = 'review-card';
    article.style.setProperty('--card-accent', `var(--${accent})`);

    const media = document.createElement('div');
    media.className = 'review-card__media';

    if (this._isSafeHttpsUrl(mapped.photoUrl)) {
      const img = document.createElement('img');
      img.setAttribute('src', mapped.photoUrl);
      img.setAttribute('loading', 'lazy');
      img.setAttribute('alt', '');
      img.className = 'review-card__photo';
      media.appendChild(img);
    } else {
      const noPhoto = document.createElement('div');
      noPhoto.className = 'review-card__media--no-photo';
      noPhoto.setAttribute('aria-hidden', 'true');
      noPhoto.appendChild(this._buildIconSvg('quote', 48, 'review-card__no-photo-mark'));
      media.appendChild(noPhoto);
    }

    const bar = document.createElement('span');
    bar.className = 'review-card__accent-bar';
    bar.setAttribute('aria-hidden', 'true');
    media.appendChild(bar);

    const body = document.createElement('div');
    body.className = 'review-card__body';

    const stars = document.createElement('span');
    stars.className = 'review-card__stars';
    stars.setAttribute('aria-label', `${mapped.rating} out of 5 stars`);
    for (let i = 0; i < mapped.rating; i++) {
      stars.appendChild(this._buildIconSvg('star-filled', 16, 'review-card__star'));
    }
    body.appendChild(stars);

    const quote = document.createElement('p');
    quote.className = 'review-card__quote';
    quote.appendChild(this._buildIconSvg('quote', 18, 'review-card__quote-mark'));
    quote.appendChild(document.createTextNode(' ' + mapped.quote));
    body.appendChild(quote);

    const footer = document.createElement('div');
    footer.className = 'review-card__footer';
    const name = document.createElement('span');
    name.className = 'review-card__name';
    name.textContent = mapped.name;
    footer.appendChild(name);
    if (mapped.tag) {
      const tag = document.createElement('span');
      tag.className = 'review-card__tag';
      tag.textContent = mapped.tag;
      footer.appendChild(tag);
    }
    body.appendChild(footer);

    article.appendChild(media);
    article.appendChild(body);
    return article;
  }

  _buildIconSvg(name, size, className) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', `icon icon-${name} ${className}`);
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));
    svg.setAttribute('aria-hidden', 'true');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    const spriteUrl = document.documentElement.dataset.iconSprite || '';
    use.setAttribute('href', `${spriteUrl}#icon-${name}`);
    svg.appendChild(use);
    return svg;
  }

  _isSafeHttpsUrl(url) {
    if (typeof url !== 'string' || url.length === 0) return false;
    try {
      return new URL(url, window.location.href).protocol === 'https:';
    } catch {
      return false;
    }
  }

  _formatCount(count) {
    try {
      const locale = document.documentElement.lang || 'en-PH';
      return new Intl.NumberFormat(locale).format(count);
    } catch {
      return String(count);
    }
  }

  _showLoadMore() {
    if (!this._loadMoreBtn) return;
    this._loadMoreBtn.setAttribute('data-visible', 'true');
  }

  _hideLoadMore() {
    if (!this._loadMoreBtn) return;
    this._loadMoreBtn.setAttribute('data-visible', 'false');
  }

  _onLoadMoreClick = async () => {
    if (!this._loadMoreBtn) return;
    if (this._currentPage >= this._lastPage) {
      this._hideLoadMore();
      return;
    }
    const nextPage = this._currentPage + 1;
    this._loadMoreBtn.setAttribute('disabled', '');
    const originalLabel = this._loadMoreBtn.textContent;
    this._loadMoreBtn.textContent = 'Loading...';
    try {
      const page = await this._loadFeaturedPage(nextPage);
      if (page) {
        this._appendPage(page);
        this._currentPage = page.meta ? page.meta.current_page : nextPage;
        this._lastPage = page.meta ? page.meta.last_page : this._lastPage;
      }
    } finally {
      this._loadMoreBtn.textContent = originalLabel;
      this._loadMoreBtn.removeAttribute('disabled');
      if (this._currentPage >= this._lastPage) this._hideLoadMore();
    }
  };

  _showFallback(reason) {
    const fallback = this.querySelector(':scope > .reviews-wall__seed-fallback');
    if (fallback) fallback.removeAttribute('hidden');
    if (window.console && console.warn) {
      console.warn('[reviews-wall]', 'fetch-failed', reason || '');
    }
  }
}

if (!customElements.get('reviews-wall')) {
  customElements.define('reviews-wall', ReviewsWall);
}
